import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { CreditCard, CheckCircle2, AlertTriangle, ExternalLink, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StripeConnectCard({ token }) {
  const { toast } = useToast();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/connect/status`, auth).then((r) => setStatus(r.data)).catch(() => setStatus({ error: 'Could not load status' }));
  useEffect(() => { if (token) load(); }, [token]);

  const connect = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/connect/account`, {}, auth);
      const { data } = await axios.post(`${API}/connect/onboarding-link`, {}, auth);
      window.location.assign(data.url);
    } catch (e) {
      toast({ title: 'Could not start Stripe onboarding', description: e.response?.data?.detail || e.message, variant: 'destructive' });
      setBusy(false);
    }
  };

  const ready = status?.connected && status?.charges_enabled;
  return (
    <Card className="shadow-lg" data-testid="stripe-connect-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-blue-600" /> Online payments (Stripe)</CardTitle>
        <p className="text-sm text-gray-500">Connect your own Stripe account so customers can pay invoices from their portal link. Money goes straight to your Stripe balance — HinesTime never holds funds.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {!status ? <p className="text-sm text-gray-500">Loading…</p> : (
          <div className={`rounded-lg border p-3 text-sm flex items-start gap-2 ${ready
            ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/40 dark:border-emerald-800'
            : 'bg-amber-50 border-amber-200 dark:bg-amber-950/40 dark:border-amber-800'}`} data-testid="stripe-connect-status">
            {ready ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /> : <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />}
            <div>
              {ready ? (
                <p className="text-emerald-800 dark:text-emerald-200"><strong>Ready to accept payments.</strong> Account {status.stripe_account_id}{status.default_currency ? ` · payouts in ${status.default_currency}` : ''}.</p>
              ) : status.connected ? (
                <p className="text-amber-800 dark:text-amber-200"><strong>Onboarding incomplete.</strong> Stripe still needs details before charges are enabled — click "Continue Stripe setup".</p>
              ) : (
                <p className="text-amber-800 dark:text-amber-200"><strong>Not connected.</strong> Customers see remit-to instructions only until you connect Stripe.</p>
              )}
              {!status.webhook_configured && status.connected && (
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-1" data-testid="stripe-connect-webhook-warning">Payment confirmations still work via the customer's return page; ask your HinesTime admin to add the Connect webhook secret for background confirmation.</p>
              )}
              {status.error && <p className="text-xs text-red-600 dark:text-red-400 mt-1">{status.error}</p>}
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button onClick={connect} disabled={busy || !status?.stripe_configured} data-testid="stripe-connect-btn">
            <ExternalLink className="w-4 h-4 mr-1.5" /> {busy ? 'Opening Stripe…' : ready ? 'Update Stripe details' : status?.connected ? 'Continue Stripe setup' : 'Connect Stripe'}
          </Button>
          <Button variant="outline" onClick={load} data-testid="stripe-connect-refresh"><RefreshCw className="w-4 h-4 mr-1.5" /> Refresh status</Button>
        </div>
        <p className="text-xs text-gray-500">Per customer you choose <em>Pay by invoice</em>, <em>Stripe</em> or <em>Both</em> (Customers → edit). Invoices are charged in the invoice's currency; your Stripe account must support that currency.</p>
      </CardContent>
    </Card>
  );
}
